import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AffiliatePartnerRow, AffiliatePayoutRow } from "./types";

export function AffiliatePayoutsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [partnerId, setPartnerId] = useState<string>("");
  const [quarter, setQuarter] = useState(() => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${q}`;
  });
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const partnersQuery = useQuery<AffiliatePartnerRow[]>({
    queryKey: ["/api/admin/affiliate-partners"],
    queryFn: async () => {
      const res = await fetch("/api/admin/affiliate-partners", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const payoutsQuery = useQuery<AffiliatePayoutRow[]>({
    queryKey: ["/api/admin/affiliate-payouts"],
    queryFn: async () => {
      const res = await fetch("/api/admin/affiliate-payouts", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const cents = Math.round(parseFloat(amount || "0") * 100);
      const res = await fetch("/api/admin/affiliate-payouts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partnerId: Number(partnerId),
          periodQuarter: quarter.trim(),
          amountCents: Number.isFinite(cents) ? cents : 0,
          notes: notes.trim() || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to create payout");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/affiliate-payouts"] });
      toast({ title: "Payout recorded" });
      setAmount("");
      setNotes("");
    },
    onError: (err: unknown) => toast({ title: err instanceof Error ? err.message : "Failed to create payout", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await fetch(`/api/admin/affiliate-payouts/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to update");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/affiliate-payouts"] });
      toast({ title: "Payout updated" });
    },
    onError: (err: unknown) => toast({ title: err instanceof Error ? err.message : "Failed to update payout", variant: "destructive" }),
  });

  const partners = partnersQuery.data || [];
  const payouts = payoutsQuery.data || [];
  const partnerById = new Map(partners.map(p => [p.id, p]));

  function partnerLabel(p: AffiliatePartnerRow) {
    return p.contactName || p.companyName || p.email || `Partner #${p.id}`;
  }

  function formatCents(cents: number) {
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  }

  const statusColors: Record<string, string> = {
    pending: "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200",
    paid: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300",
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-500" />
          Affiliate Payouts
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Manual quarterly payout ledger for affiliate &amp; Founder Circle partners. Record a
          payout amount per partner per quarter, then mark it <strong>Paid</strong> once the money
          has gone out. This is a manual stub — no automated bank transfer is triggered.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 items-end">
          <div className="md:col-span-2">
            <label className="text-sm font-medium block mb-1">Partner</label>
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger data-testid="select-affiliate-partner">
                <SelectValue placeholder="Select a partner" />
              </SelectTrigger>
              <SelectContent>
                {partners.map(p => (
                  <SelectItem key={p.id} value={String(p.id)} data-testid={`option-partner-${p.id}`}>
                    {partnerLabel(p)} ({p.partnerType === "founder_circle" ? "Founder" : "Affiliate"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Quarter</label>
            <Input value={quarter} onChange={e => setQuarter(e.target.value)} placeholder="2026-Q1" data-testid="input-payout-quarter" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Amount ($)</label>
            <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" inputMode="decimal" data-testid="input-payout-amount" />
          </div>
          <div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!partnerId || !quarter.trim() || createMutation.isPending}
              className="gap-2 w-full"
              data-testid="button-create-payout"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
              Record Payout
            </Button>
          </div>
          <div className="md:col-span-5">
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="input-payout-notes" />
          </div>
        </div>

        {payoutsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : payouts.length === 0 ? (
          <p className="text-muted-foreground text-center py-8" data-testid="text-no-payouts">No payouts recorded yet.</p>
        ) : (
          <Table data-testid="table-affiliate-payouts">
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>Quarter</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.map(p => {
                const partner = partnerById.get(p.partnerId);
                return (
                  <TableRow key={p.id} data-testid={`row-payout-${p.id}`}>
                    <TableCell>{partner ? partnerLabel(partner) : `Partner #${p.partnerId}`}</TableCell>
                    <TableCell>{p.periodQuarter}</TableCell>
                    <TableCell className="font-medium">{formatCents(p.amountCents)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[p.status] || "bg-gray-100 text-gray-600"}`}>
                        {p.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{p.notes || "—"}</TableCell>
                    <TableCell className="text-right">
                      {p.status === "pending" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateMutation.mutate({ id: p.id, status: "paid" })}
                          disabled={updateMutation.isPending}
                          data-testid={`button-mark-paid-${p.id}`}
                        >
                          Mark Paid
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateMutation.mutate({ id: p.id, status: "pending" })}
                          disabled={updateMutation.isPending}
                          data-testid={`button-mark-pending-${p.id}`}
                        >
                          Undo
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
